import React, {
	useState,
	useEffect,
	useCallback,
	memo,
	type ElementRef,
	type MouseEvent,
	type KeyboardEvent,
	useImperativeHandle,
} from 'react';
import { styled } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { openViewFromEmbed } from '@atlassian/jira-business-common';
import { SELECTED_ISSUE_PARAM } from '@atlassian/jira-business-constants';
import { fg } from '@atlassian/jira-feature-gating';
import { useEditField } from '@atlassian/jira-issue-field-base';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { convertAdfStringToObject } from '@atlassian/jira-rich-content';
import type { CellNavigationProps } from '../../../common/types/cell-navigation';
import { WrapperCommon } from '../../../common/ui/list-cell';
import { getADFMarkup } from '../../../common/utils/get-adf-markup';
import { useJWMListFeatures } from '../../../controllers/features-context';

type TextAreaCellProps = {
	issueKey: string;
	fieldKey: string;
	fieldType: string;
	fieldValue: string | null;
	onClick: (event: MouseEvent | KeyboardEvent) => void;
} & CellNavigationProps;

export const TextAreaCell = memo<TextAreaCellProps>(
	({
		fieldKey,
		fieldType,
		fieldValue,
		onClick,
		issueKey,
		isSelected = false,
		wrapperRef,
		onFocus,
		onBlur,
		onKeyDown,
		cellRef,
	}: TextAreaCellProps) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { issueViewInteraction } = useJWMListFeatures();
		const [textValue, setTextValue] = useState<string>('');

		const onEmbedClick = useCallback(() => {
			fireUIAnalytics(createAnalyticsEvent({}), 'textAreaCell clicked', 'listEmbedIssue');
			openViewFromEmbed({ key: SELECTED_ISSUE_PARAM, value: issueKey });
		}, [createAnalyticsEvent, issueKey]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated wrapperRef is now managed in CellNavigation
		 */
		const onWrapperRef = useCallback(
			(ref: ElementRef<'div'> | null) => {
				if (wrapperRef) {
					// eslint-disable-next-line no-param-reassign
					wrapperRef.current = ref;
				}
			},
			[wrapperRef],
		);

		const [{ value: extractedFieldValue }] = useEditField<string | null, null>({
			fieldKey,
			issueKey,
			fieldType,
			initialValue: fieldValue,
		});

		const cellValue = fg('jwm_list_new_copy_paste_behaviour') ? extractedFieldValue : fieldValue;

		// keeping value up-to-date with bento value
		// text area value is type string
		useEffect(() => {
			// TODO: JEH-62
			getADFMarkup(convertAdfStringToObject(cellValue)).then((value) => setTextValue(value));
		}, [cellValue]);

		const handleClick = useCallback(
			(event: MouseEvent | KeyboardEvent) => {
				issueViewInteraction === 'openNewTab' ? onEmbedClick() : onClick(event);
			},
			[issueViewInteraction, onClick, onEmbedClick],
		);

		const handleKeyDown = useCallback(
			(event: KeyboardEvent<HTMLElement>) => {
				if (event.code === 'Enter') {
					handleClick(event);
					return true;
				}
				return false;
			},
			[handleClick],
		);

		const cellOverrides = useCallback(
			() => ({ onClick: () => true, onKeyDown: handleKeyDown }),
			[handleKeyDown],
		);
		useImperativeHandle(cellRef, cellOverrides, [cellOverrides]);

		if (fg('jira_list_keyboard_nav')) {
			return (
				<Box xcss={wrapperStyles} onClick={handleClick}>
					<Box xcss={textStyles} as="span">
						{textValue}
					</Box>
				</Box>
			);
		}

		return (
			<Wrapper
				isFocused={isSelected}
				onClick={handleClick}
				onBlur={onBlur}
				tabIndex={0}
				onFocus={onFocus}
				onKeyDown={onKeyDown}
				ref={onWrapperRef}
			>
				<span>{textValue}</span>
			</Wrapper>
		);
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Wrapper = styled(WrapperCommon)({
	cursor: 'pointer',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	span: {
		paddingLeft: token('space.100', '8px'),
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
});

Wrapper.displayName = 'Wrapper';

const wrapperStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	height: '100%',
	cursor: 'pointer',
});

const textStyles = xcss({
	padding: 'space.075',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});
